/**
 * Les écrans d'accès : connexion, mot de passe oublié, inscription, nouveau mot de passe.
 *
 * Ils vivent dans un seul fichier parce qu'ils partagent la même mise en page — un panneau
 * centré, un titre, un formulaire — et qu'un utilisateur passe de l'un à l'autre. Les séparer
 * en quatre fichiers aurait multiplié la même enveloppe par quatre.
 *
 * ## Ce qu'aucun de ces écrans ne fait
 *
 * **Aucun ne dit si une adresse existe.** L'écran de mot de passe oublié affiche la même phrase
 * dans tous les cas, et la connexion ne distingue pas « adresse inconnue » de « mot de passe
 * faux ». C'est le serveur qui tient cette règle ; l'écran ne la contredit pas en étant plus
 * bavard que lui.
 */
import { useEffect, useState } from "react";
import type { JSX } from "react";
import {
  accepterInvitation,
  demanderReinitialisation,
  lireInvitation,
  poserMotDePasse,
  seConnecter,
} from "./api";
import type { Compte, InvitationLue } from "./api";

/** Le minimum du serveur, redit ici pour que l'écran n'envoie pas ce qui sera refusé. */
const LONGUEUR_MINIMALE = 8;

function Panneau({
  titre,
  soustitre,
  children,
}: {
  titre: string;
  soustitre?: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <main className="acces">
      <section className="acces__panneau">
        <h1>{titre}</h1>
        {soustitre !== undefined && <p className="note">{soustitre}</p>}
        {children}
      </section>
    </main>
  );
}

/**
 * Le double champ de mot de passe.
 *
 * **La confirmation n'est pas une politesse** : ces trois écrans posent un mot de passe qui
 * n'existe pas encore, donc une faute de frappe n'est rattrapable que par « mot de passe
 * oublié ». Là où l'ancien mot de passe est demandé, la confirmation ne sert plus à rien.
 */
function ChampsMotDePasse({
  valeur,
  confirmation,
  enAttente,
  onValeur,
  onConfirmation,
}: {
  valeur: string;
  confirmation: string;
  enAttente: boolean;
  onValeur: (v: string) => void;
  onConfirmation: (v: string) => void;
}): JSX.Element {
  const tropCourt = valeur !== "" && valeur.length < LONGUEUR_MINIMALE;
  const different = confirmation !== "" && confirmation !== valeur;

  return (
    <>
      <label>
        Mot de passe
        <input
          type="password"
          className="saisie saisie--texte"
          value={valeur}
          autoComplete="new-password"
          disabled={enAttente}
          onChange={(e) => {
            onValeur(e.target.value);
          }}
        />
      </label>
      {tropCourt && (
        <p className="note">
          {LONGUEUR_MINIMALE} caractères au minimum. Une phrase longue vaut mieux qu'un mot
          compliqué.
        </p>
      )}
      <label>
        Le répéter
        <input
          type="password"
          className="saisie saisie--texte"
          value={confirmation}
          autoComplete="new-password"
          disabled={enAttente}
          onChange={(e) => {
            onConfirmation(e.target.value);
          }}
        />
      </label>
      {different && <p className="alerte">Les deux saisies diffèrent.</p>}
    </>
  );
}

export function motDePasseUtilisable(valeur: string, confirmation: string): boolean {
  return valeur.length >= LONGUEUR_MINIMALE && valeur === confirmation;
}

/* ---------------------------------------------------------------------------
 * Connexion
 * ------------------------------------------------------------------------- */

export function Connexion({ onConnecte }: { onConnecte: (compte: Compte) => void }): JSX.Element {
  const [courriel, setCourriel] = useState("");
  const [motDePasse, setMotDePasse] = useState("");
  const [oubli, setOubli] = useState(false);
  const [enAttente, setEnAttente] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  if (oubli) {
    return (
      <Panneau
        titre="Mot de passe oublié"
        soustitre="Indiquez votre adresse professionnelle : un lien pour choisir un nouveau mot de passe vous sera envoyé."
      >
        {erreur !== null && <p className="alerte">{erreur}</p>}
        {message !== null && <p className="etat">{message}</p>}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setEnAttente(true);
            setErreur(null);
            demanderReinitialisation(courriel)
              .then((r) => {
                setMessage(r.message);
              })
              .catch((x: unknown) => {
                setErreur(x instanceof Error ? x.message : String(x));
              })
              .finally(() => {
                setEnAttente(false);
              });
          }}
        >
          <label>
            Adresse électronique
            <input
              type="email"
              className="saisie saisie--texte"
              value={courriel}
              autoComplete="username"
              disabled={enAttente}
              onChange={(e) => {
                setCourriel(e.target.value);
              }}
            />
          </label>
          <div className="acces__gestes">
            <button type="submit" disabled={enAttente || courriel.trim() === ""}>
              {enAttente ? "Envoi…" : "Envoyer le lien"}
            </button>
            <button
              type="button"
              className="lien"
              onClick={() => {
                setOubli(false);
                setMessage(null);
                setErreur(null);
              }}
            >
              Revenir à la connexion
            </button>
          </div>
        </form>
      </Panneau>
    );
  }

  return (
    <Panneau titre="Planification des chantiers" soustitre="Connectez-vous pour continuer.">
      {erreur !== null && (
        <p className="alerte" role="alert">
          {erreur}
        </p>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setEnAttente(true);
          setErreur(null);
          seConnecter(courriel, motDePasse)
            .then((r) => {
              onConnecte(r.compte);
            })
            .catch((x: unknown) => {
              setErreur(x instanceof Error ? x.message : String(x));
              // Le mot de passe est effacé, l'adresse gardée : c'est le mot de passe qu'on
              // retape, et réécrire son adresse à chaque essai est une punition inutile.
              setMotDePasse("");
            })
            .finally(() => {
              setEnAttente(false);
            });
        }}
      >
        <label>
          Adresse électronique
          <input
            type="email"
            className="saisie saisie--texte"
            value={courriel}
            autoComplete="username"
            disabled={enAttente}
            onChange={(e) => {
              setCourriel(e.target.value);
            }}
          />
        </label>
        <label>
          Mot de passe
          <input
            type="password"
            className="saisie saisie--texte"
            value={motDePasse}
            autoComplete="current-password"
            disabled={enAttente}
            onChange={(e) => {
              setMotDePasse(e.target.value);
            }}
          />
        </label>
        <div className="acces__gestes">
          <button type="submit" disabled={enAttente || courriel.trim() === "" || motDePasse === ""}>
            {enAttente ? "Connexion…" : "Se connecter"}
          </button>
          <button
            type="button"
            className="lien"
            onClick={() => {
              setOubli(true);
              setErreur(null);
            }}
          >
            Mot de passe oublié
          </button>
        </div>
      </form>
      <p className="note">
        Seules les adresses enregistrées au référentiel des salariés peuvent avoir un compte, et
        c'est l'administrateur qui les invite.
      </p>
    </Panneau>
  );
}

/* ---------------------------------------------------------------------------
 * Inscription depuis une invitation
 * ------------------------------------------------------------------------- */

type EtatInvitation =
  | { phase: "chargement" }
  | { phase: "lue"; invitation: InvitationLue }
  | { phase: "refusee"; raison: string }
  | { phase: "creee"; message: string };

export function Inscription({ jeton }: { jeton: string }): JSX.Element {
  const [etat, setEtat] = useState<EtatInvitation>({ phase: "chargement" });
  const [motDePasse, setMotDePasse] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [enAttente, setEnAttente] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    lireInvitation(jeton)
      .then((r) => {
        setEtat({ phase: "lue", invitation: r.invitation });
      })
      .catch((x: unknown) => {
        setEtat({ phase: "refusee", raison: x instanceof Error ? x.message : String(x) });
      });
  }, [jeton]);

  if (etat.phase === "chargement") {
    return <Panneau titre="Votre accès">Vérification de l'invitation…</Panneau>;
  }

  if (etat.phase === "refusee") {
    return (
      <Panneau titre="Invitation inutilisable">
        <p className="alerte">{etat.raison}</p>
        <p className="note">
          Demandez une nouvelle invitation à l'administrateur, ou connectez-vous si votre compte
          existe déjà.
        </p>
        <a className="lien" href="/">
          Aller à la connexion
        </a>
      </Panneau>
    );
  }

  if (etat.phase === "creee") {
    return (
      <Panneau titre="Compte créé">
        <p className="etat">{etat.message}</p>
        <a className="lien" href="/">
          Aller à la connexion
        </a>
      </Panneau>
    );
  }

  return (
    <Panneau
      titre={`Bienvenue ${etat.invitation.prenom}`}
      soustitre={`Choisissez votre mot de passe pour ${etat.invitation.courriel}.`}
    >
      {erreur !== null && <p className="alerte">{erreur}</p>}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!motDePasseUtilisable(motDePasse, confirmation)) return;
          setEnAttente(true);
          setErreur(null);
          accepterInvitation(jeton, motDePasse)
            .then((r) => {
              setEtat({ phase: "creee", message: r.message });
            })
            .catch((x: unknown) => {
              setErreur(x instanceof Error ? x.message : String(x));
            })
            .finally(() => {
              setEnAttente(false);
            });
        }}
      >
        <ChampsMotDePasse
          valeur={motDePasse}
          confirmation={confirmation}
          enAttente={enAttente}
          onValeur={setMotDePasse}
          onConfirmation={setConfirmation}
        />
        <div className="acces__gestes">
          <button
            type="submit"
            disabled={enAttente || !motDePasseUtilisable(motDePasse, confirmation)}
          >
            {enAttente ? "Création…" : "Créer mon compte"}
          </button>
        </div>
      </form>
      <p className="note">
        Votre compte devra ensuite être approuvé par l'administrateur avant la première connexion.
      </p>
    </Panneau>
  );
}

/* ---------------------------------------------------------------------------
 * Nouveau mot de passe depuis un lien
 * ------------------------------------------------------------------------- */

export function NouveauMotDePasse({ jeton }: { jeton: string }): JSX.Element {
  const [motDePasse, setMotDePasse] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [enAttente, setEnAttente] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  if (message !== null) {
    return (
      <Panneau titre="Mot de passe enregistré">
        <p className="etat">{message}</p>
        <a className="lien" href="/">
          Aller à la connexion
        </a>
      </Panneau>
    );
  }

  return (
    <Panneau titre="Choisir un mot de passe">
      {erreur !== null && <p className="alerte">{erreur}</p>}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!motDePasseUtilisable(motDePasse, confirmation)) return;
          setEnAttente(true);
          setErreur(null);
          poserMotDePasse(jeton, motDePasse)
            .then((r) => {
              setMessage(r.message);
            })
            .catch((x: unknown) => {
              setErreur(x instanceof Error ? x.message : String(x));
            })
            .finally(() => {
              setEnAttente(false);
            });
        }}
      >
        <ChampsMotDePasse
          valeur={motDePasse}
          confirmation={confirmation}
          enAttente={enAttente}
          onValeur={setMotDePasse}
          onConfirmation={setConfirmation}
        />
        <div className="acces__gestes">
          <button
            type="submit"
            disabled={enAttente || !motDePasseUtilisable(motDePasse, confirmation)}
          >
            {enAttente ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </form>
      <p className="note">
        Toutes vos sessions ouvertes seront fermées : c'est voulu, et c'est ce qui rend ce geste
        utile quand on pense que son mot de passe est connu de quelqu'un d'autre.
      </p>
    </Panneau>
  );
}
